import {useState, useEffect} from 'react';
import {GoogleMap, Polygon, Marker} from '@react-google-maps/api';
import {Button} from './ui/button';
import {Input} from './ui/input';
import {Label} from './ui/label';
import {Dialog, DialogTitle, DialogDescription, DialogContent, DialogHeader, DialogFooter} from './ui/dialog';
import {Checkbox} from './ui/checkbox';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {Plus, Download, Save, MapPin, Trash2, AlertTriangle, X} from 'lucide-react';
import {CloseButton} from './ui/close-button';
import {Zone} from '../types';
import {KMLParser} from '../utils/kmlParser';
import {getPhoneErrorMessage, getEmailErrorMessage, formatPhoneNumber} from '../utils/validation';
import {useToast} from '../contexts/ToastContext';
import {useLoading} from '../contexts/LoadingContext';
import {useAuth} from '../hooks/useAuth';

interface ZoneContact {
  id: string;
  name: string;
  phone: string;
  email: string;
  sendSms: boolean;
}

interface ZoneDetailProps {
  zone: Zone;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (zoneId: string, zoneData: Partial<Zone>) => Promise<void>;
  onDelete?: (zoneId: string) => void;
}

const defaultCenter = {
  lat: 49.4144,
  lng: 2.8322,
};

export default function ZoneDetail({zone, isOpen, onClose, onUpdate, onDelete}: ZoneDetailProps) {
  const [zoneName, setZoneName] = useState(zone.name);
  const [contacts, setContacts] = useState<ZoneContact[]>([]);
  const [coordinates, setCoordinates] = useState<Array<{latitude: number; longitude: number}>>(zone.coordinates);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom] = useState(13);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [contactErrors, setContactErrors] = useState<Record<string, {phone?: string; email?: string}>>({});

  const toast = useToast();
  const {withLoading} = useLoading();
  const {isAdmin, isZoneManager} = useAuth();
  


  // Initialize form with zone data
  useEffect(() => {
    if (zone) {
      setZoneName(zone.name);
      setCoordinates(zone.coordinates);
      
      // Convert zone contacts to internal format
      const initialContacts: ZoneContact[] = [];
      if (zone.contacts && zone.contacts.length > 0) {
        zone.contacts.forEach((contact, index) => {
          initialContacts.push({
            id: `contact_${index}_${Date.now()}`,
            name: contact.name,
            phone: contact.phone,
            email: contact.email,
            sendSms: contact.sendSms || false
          });
        });
      } else {
        // Ensure at least one contact
        initialContacts.push({
          id: `contact_${Date.now()}`,
          name: '',
          phone: '',
          email: '',
          sendSms: false
        });
      }
      setContacts(initialContacts);

      // Center map on zone
      if (zone.coordinates.length > 0) {
        const bounds = new google.maps.LatLngBounds();
        zone.coordinates.forEach(coord => {
          bounds.extend({lat: coord.latitude, lng: coord.longitude});
        });

        const center = bounds.getCenter();
        if (center) {
          const newCenter = {lat: center.lat(), lng: center.lng()};
          setMapCenter(newCenter);
          
          // We'll use fitBounds in the onLoad callback instead of setting a fixed zoom
        }
      }
    }
  }, [zone]);

  // Effect to fit bounds when map and coordinates are ready
  useEffect(() => {
    if (map && coordinates.length > 0) {
      
      // Petit délai pour laisser le temps au Polygon de se rendre
      setTimeout(() => {
        const bounds = new google.maps.LatLngBounds();
        coordinates.forEach(coord => {
          bounds.extend({lat: coord.latitude, lng: coord.longitude});
        });
        
        // Add some padding around the zone
        map.fitBounds(bounds, {
          top: 50,
          right: 50,
          bottom: 50,
          left: 50
        });
      }, 100);
    }
  }, [map, coordinates]);

  // Ajouter un contact
  const addContact = () => {
    const newContact: ZoneContact = {
      id: `contact_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      name: '',
      phone: '',
      email: '',
      sendSms: false
    };
    setContacts([...contacts, newContact]);
  };

  // Supprimer un contact
  const removeContact = (contactId: string) => {
    setContacts(contacts.filter(c => c.id !== contactId));
  };

  // Mettre à jour un contact
  const updateContact = (contactId: string, field: keyof Omit<ZoneContact, 'id'>, value: string | boolean) => {
    let finalValue: any = value;
    
    // Formater automatiquement le numéro de téléphone pendant la saisie
    if (field === 'phone' && typeof value === 'string') {
      finalValue = formatPhoneNumber(value);
    }
    
    setContacts(contacts.map(contact =>
      contact.id === contactId
        ? {...contact, [field]: finalValue}
        : contact
    ));
    
    // Supprimer les erreurs lors de la modification (validation seulement au submit)
    if (contactErrors[contactId] && (field === 'phone' || field === 'email')) {
      setContactErrors(prev => ({
        ...prev,
        [contactId]: {
          ...prev[contactId],
          [field]: undefined
        }
      }));
    }
  };

  // Import KML
  const handleImportKML = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.kml';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      try {
        const result = await withLoading(
          () => KMLParser.parseKMLFile(file),
          'Analyse du fichier KML...'
        );

        if (!result.isValid) {
          toast.error('Fichier KML invalide', result.error || 'Format non supporté');
          return;
        }

        if (!result.zones || result.zones.length === 0) {
          toast.error('Aucune zone trouvée', 'Le fichier KML ne contient pas de zones valides');
          return;
        }

        // Prendre la première zone du fichier
        const firstZone = result.zones[0];
        setZoneName(firstZone.name);
        // Remplacer complètement les coordonnées existantes
        setCoordinates([...firstZone.coordinates]);

        // Centrer la carte sur la zone
        if (firstZone.coordinates.length > 0) {
          const bounds = new google.maps.LatLngBounds();
          firstZone.coordinates.forEach(coord => {
            bounds.extend({lat: coord.latitude, lng: coord.longitude});
          });

          if (map) {
            map.fitBounds(bounds, {
              top: 50,
              right: 50,
              bottom: 50,
              left: 50
            });
            const center = bounds.getCenter();
            if (center) {
              setMapCenter({lat: center.lat(), lng: center.lng()});
            }
          }
        }

      } catch (error) {
        console.error('Erreur lors de l\'import KML:', error);
        toast.error('Erreur d\'import', 'Impossible d\'analyser le fichier KML');
      }
    };
    input.click();
  };

  // Gérer les clics sur la carte
  const handleMapClick = (e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;

    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    const newPoint = {latitude: lat, longitude: lng};

    setCoordinates([...coordinates, newPoint]);
  };

  // Gérer le clic sur un marqueur pour le supprimer
  const handleMarkerClick = (index: number) => {
    const newCoordinates = coordinates.filter((_, i) => i !== index);
    setCoordinates(newCoordinates);

    if (newCoordinates.length < 3) {
      toast.info('Zone incomplète', 'Au moins 3 points sont nécessaires pour créer une zone');
    }
  };

  // Gérer le drag & drop d'un marqueur
  const handleMarkerDragEnd = (index: number, event: google.maps.MapMouseEvent) => {
    if (!event.latLng) return;

    const newCoordinates = [...coordinates];
    newCoordinates[index] = {
      latitude: event.latLng.lat(),
      longitude: event.latLng.lng()
    };
    setCoordinates(newCoordinates);
  };

  // Validation du formulaire (simplifiée pour ne pas bloquer le bouton)
  const isFormValid = () => {
    const isValid = (
      zoneName.trim() !== '' &&
      coordinates.length >= 3 &&
      contacts.length > 0 &&
      contacts.every(contact => {
        const nameValid = contact.name.trim() !== '';
        const phoneValid = contact.sendSms ? contact.phone.trim() !== '' : true;
        const emailValid = contact.email.trim() !== '';
        return nameValid && phoneValid && emailValid;
      })
    );
    return isValid;
  };

  // Sauvegarder les modifications
  const handleSave = async () => {
    // Valider tous les contacts
    let hasValidationErrors = false;
    const newErrors: Record<string, {phone?: string; email?: string}> = {};
    
    contacts.forEach(contact => {
      // Valider le téléphone seulement si sendSms est activé
      const phoneError = contact.sendSms ? getPhoneErrorMessage(contact.phone) : null;
      const emailError = getEmailErrorMessage(contact.email);
      
      if (phoneError || emailError) {
        hasValidationErrors = true;
        newErrors[contact.id] = {
          phone: phoneError || undefined,
          email: emailError || undefined
        };
      }
    });
    
    if (hasValidationErrors) {
      setContactErrors(newErrors);
      return;
    }
    
    if (!isFormValid()) {
      return;
    }

    try {
      const updateData: Partial<Zone> = {
        name: zoneName.trim(),
        coordinates,
        contacts: contacts.map(contact => ({
          name: contact.name.trim(),
          phone: contact.phone.trim(),
          email: contact.email.trim(),
          sendSms: contact.sendSms
        }))
      };

      await onUpdate(zone.id, updateData);
      toast.success('Zone modifiée', 'Les modifications ont été enregistrées avec succès');
      onClose();

    } catch (error) {
      console.error('Erreur lors de la modification de la zone:', error);
      toast.error('Erreur de sauvegarde', 'Impossible de modifier la zone');
    }
  };

  // Fonction pour supprimer la zone
  const handleDelete = async () => {
    if (!onDelete || !isAdmin) return;
    setIsDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!onDelete) return;
    
    try {
      await onDelete(zone.id);
      toast.success('Zone supprimée', 'La zone a été supprimée avec succès');
      setIsDeleteConfirmOpen(false);
      onClose();
    } catch (error) {
      toast.error('Erreur', 'Impossible de supprimer la zone');
      setIsDeleteConfirmOpen(false);
    }
  };

  // Obtenir la couleur de la zone
  const getZoneColor = () => {
    // Couleur par défaut pour toutes les zones
    return '#10b981';
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content className="fixed inset-0 sm:inset-auto sm:left-[50%] sm:top-[50%] z-50 w-full sm:max-w-[95vw] h-full sm:h-auto sm:max-h-[95vh] sm:translate-x-[-50%] sm:translate-y-[-50%] p-0 bg-white sm:border sm:border-gray-200 sm:rounded-lg shadow-xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 sm:data-[state=closed]:zoom-out-95 sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:slide-out-to-left-1/2 sm:data-[state=closed]:slide-out-to-top-[48%] sm:data-[state=open]:slide-in-from-left-1/2 sm:data-[state=open]:slide-in-from-top-[48%]" style={{overflow: 'visible'}}>
          <DialogTitle className="sr-only">Détails de la zone</DialogTitle>
          <DialogDescription className="sr-only">
            Interface de modification de zone avec carte interactive et gestion des contacts
          </DialogDescription>

          <div className="flex flex-col h-full sm:h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white rounded-t-lg flex-shrink-0">
              <h2 className="text-lg font-semibold">Détails de la Zone</h2>
              <CloseButton onClick={onClose} />
            </div>

            {/* Contenu scrollable */}
            <div className="flex-1 overflow-y-auto">

              {/* Instructions */}
              <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <span className="text-sm text-gray-700">
                    Cliquez sur la carte pour ajouter des points, ou sur un point pour le supprimer
                  </span>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleImportKML}
                      size="sm"
                      className="gap-2 bg-gray-900 hover:bg-gray-800 text-white"
                    >
                      <Download className="w-4 h-4" />
                      Import KML
                    </Button>
                    {isAdmin && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleDelete}
                        className="gap-2 bg-red-500 hover:bg-red-600 text-white"
                        title="Supprimer cette zone"
                      >
                        <Trash2 className="w-4 h-4" />
                        Supprimer
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Carte en format paysage */}
              <div className="relative h-[300px] sm:h-[450px] border-b border-gray-200">
              <GoogleMap
                mapContainerStyle={{width: '100%', height: '100%'}}
                center={mapCenter}
                zoom={mapZoom}
                onLoad={(mapInstance) => {
                  setMap(mapInstance);
                  // Attendre un peu avant de rendre les composants sur la carte
                  setTimeout(() => {
                    setMapReady(true);
                  }, 500);
                }}
                onClick={handleMapClick}
                options={{
                  disableDefaultUI: false,
                  zoomControl: true,
                  draggableCursor: 'crosshair',
                }}
              >
                {/* Zone existante/modifiée - automatiquement fermée */}
                {mapReady && coordinates.length >= 3 && (
                  <>
                    <Polygon
                      paths={coordinates.map(coord => ({lat: coord.latitude, lng: coord.longitude}))}
                      options={{
                        fillColor: getZoneColor(),
                        fillOpacity: 0.3,
                        strokeColor: getZoneColor(),
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                      }}
                    />
                  </>
                )}

                {/* Marqueurs pour les points */}
                {mapReady && coordinates.map((coord, index) => (
                  <Marker
                    key={index}
                    position={{lat: coord.latitude, lng: coord.longitude}}
                    draggable={true}
                    icon={{
                      path: google.maps.SymbolPath.CIRCLE,
                      fillColor: getZoneColor(),
                      fillOpacity: 1,
                      strokeColor: 'white',
                      strokeWeight: 2,
                      scale: 8,
                    }}
                    title={`Point ${index + 1} - Cliquez pour supprimer ou glissez pour déplacer`}
                    onClick={() => handleMarkerClick(index)}
                    onDragEnd={(event) => handleMarkerDragEnd(index, event)}
                    cursor="pointer"
                  />
                ))}
              </GoogleMap>
            </div>

              {/* Note sous la carte */}
              <div className="px-6 py-2 text-xs text-gray-500">
                <div className="flex items-center gap-2">
                  <MapPin className="w-3 h-3" />
                  {coordinates.length === 0 && "Cliquez sur la carte pour placer des points"}
                  {coordinates.length > 0 && coordinates.length < 3 && `${coordinates.length} point(s) placé(s), minimum 3 requis`}
                  {coordinates.length >= 3 && "Cliquez sur un point pour le supprimer ou ajoutez de nouveaux points"}
                </div>
              </div>

              {/* Formulaire en dessous */}
              <div className="p-6 space-y-6 bg-gray-50/30">
              {/* Nom de la zone */}
              <div>
                <Label htmlFor="zoneName" className="text-sm font-medium">Nom de la zone *</Label>
                <Input
                  id="zoneName"
                  value={zoneName}
                  onChange={(e) => setZoneName(e.target.value)}
                  placeholder="Ex: Forêt de Compiègne - Secteur A"
                  disabled={isZoneManager}
                  className="mt-1"
                />
              </div>

              {/* Contacts */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium">Contacts *</h3>

                <div className="space-y-3">
                  {contacts.map((contact, index) => (
                    <div key={contact.id} className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-gray-700">
                          Contact {index + 1}
                        </span>
                        {contacts.length > 1 && (
                          <Button
                            onClick={() => removeContact(contact.id)}
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs text-gray-500">Nom complet</Label>
                          <Input
                            value={contact.name}
                            onChange={(e) => updateContact(contact.id, 'name', e.target.value)}
                            placeholder="Nom complet"
                            className="mt-1 h-9"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">
                            Téléphone {!contact.sendSms && <span className="text-gray-400">(optionnel)</span>}
                          </Label>
                          <Input
                            value={contact.phone}
                            onChange={(e) => updateContact(contact.id, 'phone', e.target.value)}
                            placeholder="06 12 34 56 78"
                            className={`mt-1 h-9 ${contactErrors[contact.id]?.phone ? 'border-red-500' : ''}`}
                          />
                          {contactErrors[contact.id]?.phone && (
                            <p className="text-xs text-red-500 mt-1">{contactErrors[contact.id].phone}</p>
                          )}
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Email</Label>
                          <Input
                            type="email"
                            value={contact.email}
                            onChange={(e) => updateContact(contact.id, 'email', e.target.value)}
                            placeholder="contact@example.com"
                            className={`mt-1 h-9 ${contactErrors[contact.id]?.email ? 'border-red-500' : ''}`}
                          />
                          {contactErrors[contact.id]?.email && (
                            <p className="text-xs text-red-500 mt-1">{contactErrors[contact.id].email}</p>
                          )}
                        </div>
                      </div>
                      {/* Checkbox SMS */}
                      <label 
                        className="flex items-center justify-end space-x-2 mt-3 cursor-pointer hover:bg-gray-50 rounded px-2 py-1 -mx-2"
                        htmlFor={`sms-${contact.id}`}
                      >
                        <Checkbox
                          id={`sms-${contact.id}`}
                          checked={contact.sendSms}
                          onCheckedChange={(checked) => {
                            if (!isZoneManager) {
                              updateContact(contact.id, 'sendSms', checked);
                            }
                          }}
                          disabled={isZoneManager}
                        />
                        <span className="text-sm text-gray-700 select-none">
                          Envoyer des notifications SMS à ce contact
                        </span>
                      </label>
                    </div>
                  ))}
                </div>

                {/* Bouton ajouter contact */}
                {!isZoneManager && (
                  <div className="flex justify-end">
                    <Button
                      onClick={addContact}
                      variant="secondary"
                      size="sm"
                      className="gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Ajouter un contact
                    </Button>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="space-y-3 pt-4 border-t border-gray-200">
                {/* Boutons annuler et enregistrer */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button onClick={onClose} variant="secondary" className="flex-1">
                    Annuler
                  </Button>
                  {!isZoneManager && (
                    <Button
                      onClick={handleSave}
                      disabled={!isFormValid()}
                      className="gap-2 bg-gray-900 hover:bg-gray-800 text-white flex-1"
                    >
                      <Save className="w-4 h-4" />
                      Enregistrer les modifications
                    </Button>
                  )}
                </div>
              </div>
            </div>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>

      {/* Popup de confirmation de suppression de zone */}
      <Dialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-md bg-white border-0 shadow-2xl rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Confirmer la suppression
            </DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer cette zone ?
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-gray-50 p-4 rounded-xl border-0 shadow-sm">
              <h4 className="font-medium text-gray-900">{zone.name}</h4>
              <p className="text-sm text-gray-600">
                {zone.coordinates.length} points de délimitation
              </p>
            </div>
            <p className="text-sm text-red-600 mt-3 font-medium">
              ⚠️ Cette action est irréversible et supprimera tous les contacts associés.
            </p>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsDeleteConfirmOpen(false)}
              className="border-gray-200 shadow-sm rounded-xl"
            >
              Annuler
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 border-0 shadow-sm rounded-xl"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}